import { renderEntryLabelTemplate } from './template'
import {
  LabelPrinterDevice,
  LabelPrinterError,
  LabelPrinterStrategy,
  LabelPrintOptions,
} from './types'

export type DymoConfig = {
  serviceUrl?: string
}

const SERVICE_PORT = 41951
const SERVICE_PROTOCOLS = ['https', 'http'] as const
const SERVICE_HOSTS = ['localhost', '127.0.0.1'] as const
const SERVICE_PATHS = ['DYMO/DLS/Printing/', 'DYMO/Printing/'] as const

const DEFAULT_SERVICE_URLS: string[] = SERVICE_PROTOCOLS.flatMap(protocol =>
  SERVICE_HOSTS.flatMap(host =>
    SERVICE_PATHS.map(path => `${protocol}://${host}:${SERVICE_PORT}/${path}`)
  )
)

const HTTPS_CERTIFICATE_URL = 'https://localhost:41951/'

type ApiVariant = {
  printersPath: string
  printPath: string
}

const API_VARIANTS: ApiVariant[] = [
  { printersPath: 'GetPrinters', printPath: 'PrintLabel' },
  { printersPath: 'Printers', printPath: 'PrintLabel' },
]

type PrinterResponse = {
  printers?: Array<{
    name: string
    modelName?: string
    isConnected?: boolean
  }>
}

async function request(url: string, init?: RequestInit): Promise<Response> {
  let response: Response
  const headers = new Headers(init?.headers ?? undefined)
  if (
    !headers.has('Content-Type') &&
    init?.body !== undefined &&
    typeof init.body === 'string' &&
    init.body.trim().startsWith('{')
  ) {
    headers.set('Content-Type', 'application/json;charset=UTF-8')
  }

  try {
    response = await fetch(url, {
      headers,
      ...init,
    })
  } catch (error) {
    throw new LabelPrinterError('Unable to reach DYMO Connect Web Service. Is it running?', error)
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new LabelPrinterError(
      `DYMO service responded with ${response.status}. ${text || 'Check the service logs.'}`
    )
  }

  return response
}

function coerceBaseUrl(input?: string): string {
  if (!input) return ''
  return input.endsWith('/') ? input : `${input}/`
}

function normalizeBoolean(input: string | null | undefined): boolean | undefined {
  if (!input) return undefined
  const normalized = input.trim().toLowerCase()
  if (normalized === 'true') return true
  if (normalized === 'false') return false
  return undefined
}

function parsePrintersXml(xml: string): PrinterResponse['printers'] {
  if (typeof DOMParser === 'undefined') {
    throw new LabelPrinterError('Browser does not support XML parsing for DYMO responses.')
  }

  const parser = new DOMParser()
  const document = parser.parseFromString(xml, 'application/xml')
  const errorNode = document.querySelector('parsererror')
  if (errorNode) {
    throw new LabelPrinterError('DYMO service returned an unexpected XML response.')
  }

  const printerTags = [
    'LabelWriterPrinter',
    'LabelWriterPrinters',
    'Printer',
    'PrinterInfo',
    'LabelWriterPrinterInfo',
  ]
  const printerElements = printerTags.flatMap(tag => Array.from(document.getElementsByTagName(tag)))

  const printers: PrinterResponse['printers'] = []

  for (const element of printerElements) {
    const name = element.querySelector('Name')?.textContent?.trim()
    if (!name) continue

    const modelName = element.querySelector('ModelName')?.textContent?.trim() ?? undefined
    const isConnectedText = element.querySelector('IsConnected')?.textContent
    const isConnected = normalizeBoolean(isConnectedText)

    printers.push({ name, modelName, isConnected })
  }

  return printers
}

async function parsePrinterResponse(response: Response): Promise<PrinterResponse> {
  if (response.status === 204) {
    return { printers: [] }
  }

  const contentType = response.headers.get('Content-Type') ?? ''

  if (contentType.toLowerCase().includes('json')) {
    return (await response.json()) as PrinterResponse
  }

  const text = await response.text()
  if (!text) {
    return { printers: [] }
  }

  try {
    return JSON.parse(text) as PrinterResponse
  } catch {
    // continue with XML parsing
  }

  return { printers: parsePrintersXml(text) }
}

export function createDymoLabelPrinter(config: DymoConfig = {}): LabelPrinterStrategy {
  const configuredUrl = coerceBaseUrl(config.serviceUrl)
  const candidateBaseUrls = (configuredUrl ? [configuredUrl] : DEFAULT_SERVICE_URLS).filter(Boolean)

  let activeBaseUrl: string | null = null
  let activeApiVariant: ApiVariant | null = null

  type ServiceFailure = {
    baseUrl: string
    error: unknown
  }

  const executeWithCandidates = async <T>(
    path: string,
    init?: RequestInit,
    parser: (response: Response) => Promise<T> = async () => undefined as T
  ): Promise<T> => {
    const tried = new Set<string>()
    const candidates = [
      ...(activeBaseUrl ? [activeBaseUrl] : []),
      ...candidateBaseUrls.filter(url => url !== activeBaseUrl),
    ]

    const failures: ServiceFailure[] = []

    for (const baseUrl of candidates) {
      if (tried.has(baseUrl)) continue
      tried.add(baseUrl)

      try {
        const response = await request(`${baseUrl}${path}`, init)
        const result = await parser(response)
        activeBaseUrl = baseUrl
        return result
      } catch (error) {
        failures.push({ baseUrl, error })
        if (activeBaseUrl === baseUrl) {
          activeBaseUrl = null
        }
      }
    }

    if (failures.length > 0) {
      const lastError = failures[failures.length - 1]?.error
      const triedUrls = failures.map(failure => failure.baseUrl).join(', ')
      const lastErrorMessage =
        lastError instanceof LabelPrinterError
          ? lastError.message
          : lastError instanceof Error
            ? lastError.message
            : undefined

      const includesHttpsCandidate = candidateBaseUrls.some(url =>
        url.toLowerCase().startsWith('https://')
      )

      let message =
        'Unable to reach the DYMO Connect Web Service. Ensure that the DYMO Connect Web Service is running.'

      if (includesHttpsCandidate) {
        message = `${message} If it is, click "Open DYMO service in a new tab".`
      } else if (triedUrls || lastErrorMessage) {
        // Provide minimal context when we cannot surface the TLS action.
        const details = [triedUrls ? `Tried: ${triedUrls}.` : null, lastErrorMessage].filter(
          Boolean
        )
        if (details.length > 0) {
          message = `${message} ${details.join(' ')}`
        }
      }

      throw new LabelPrinterError(message, {
        cause: lastError,
        helpUrl: includesHttpsCandidate ? HTTPS_CERTIFICATE_URL : null,
        helpLabel: 'Open DYMO service in a new tab',
      })
    }

    throw new LabelPrinterError('Unable to reach the DYMO Connect Web Service.')
  }

  const selectApiVariant = async (): Promise<{ data: PrinterResponse; variant: ApiVariant }> => {
    const variants = activeApiVariant
      ? [activeApiVariant, ...API_VARIANTS.filter(variant => variant !== activeApiVariant)]
      : API_VARIANTS

    let lastError: unknown

    for (const variant of variants) {
      try {
        const data = await executeWithCandidates<PrinterResponse>(
          variant.printersPath,
          {
            method: 'GET',
          },
          parsePrinterResponse
        )
        activeApiVariant = variant
        return { data, variant }
      } catch (error) {
        lastError = error
        if (activeApiVariant === variant) {
          activeApiVariant = null
        }
      }
    }

    if (lastError instanceof Error) {
      throw lastError
    }

    throw new LabelPrinterError('Unable to reach the DYMO Connect Web Service.')
  }

  const loadPrinters = async (): Promise<LabelPrinterDevice[]> => {
    const result = await selectApiVariant()
    const data = result.data
    const printers = (data.printers ?? [])
      .filter(printer => printer.isConnected !== false)
      .map<LabelPrinterDevice>(printer => ({
        id: printer.name,
        name: printer.name,
        model: printer.modelName,
      }))

    if (printers.length === 0) {
      throw new LabelPrinterError('No DYMO printers found. Connect a printer and try again.')
    }

    return printers
  }

  const ensureVariant = async (): Promise<ApiVariant> => {
    if (activeApiVariant) return activeApiVariant
    await loadPrinters()
    if (!activeApiVariant) {
      throw new LabelPrinterError('Unable to reach the DYMO Connect Web Service.')
    }
    return activeApiVariant
  }

  return {
    kind: 'dymo',
    label: 'DYMO LabelWriter',
    listPrinters: loadPrinters,
    async printEntryLabel(payload, options?: LabelPrintOptions) {
      const targetPrinter = options?.printer ?? (await loadPrinters())[0]
      if (!targetPrinter) {
        throw new LabelPrinterError('Select a DYMO printer before printing.')
      }

      const copies = Math.max(1, Math.floor(options?.copies ?? 1))
      const variant = await ensureVariant()
      const jobs = Math.ceil(copies / 2)
      const isOdd = copies % 2 === 1

      for (let index = 0; index < jobs; index += 1) {
        const labelXml = renderEntryLabelTemplate(
          {
            entryCode: payload.entryCode,
            classCode: payload.classCode,
            showName: payload.showName,
          },
          { blankSecond: isOdd && index === jobs - 1 }
        )

        const requestBody = new URLSearchParams({
          printerName: targetPrinter.name,
          labelXml,
          labelSetXml: '',
        })

        await executeWithCandidates(
          variant.printPath,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: requestBody.toString(),
          },
          async () => undefined
        )
      }
    },
  }
}
